import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { EconomicIndicatorContext } from "../../../contexts/EconomicIndicatorContext";
import { Checkbox, Switch } from "@mui/material";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { EconomicIndicator } from "../../../models/dto/response/ResponseCreateEconomicIndicators";
import { useTranslation } from "react-i18next";
import { EcoEfficiencyService } from "../../../service/eco-efficiency.service";
import alertService from "../../../components/alert/alertService";
import { useAuth } from "../../../contexts/UserContext";
import { common, economic, report } from "../../../i18n";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";

export const TableEconomicIndicator = () => {
  const { t } = useTranslation([common, economic, report], {
    nsMode: "fallback",
  });

  const {
    economicIndicators,
    setEconomicIndicators,
    setEditData,
    setModalIsOpen,
  } = EconomicIndicatorContext();

  const [rows, setRows] = useState<EconomicIndicator[]>(economicIndicators);

  const { isAdmin } = useAuth();

  // ✅ เอาคอลัมน์ Actions (ไอคอน Edit) ออก
  const columns: GridColDef<EconomicIndicator>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      minWidth: 80,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value) => `EI-${value}`,
    },
    {
      field: "nameEn",
      headerName: `${t("Indicator-Name-En")}`,
      disableColumnMenu: true,
      minWidth: 180,
      type: "string",
      flex: 1,
    },
    {
      field: "nameTh",
      headerName: `${t("Indicator-Name-Th")}`,
      disableColumnMenu: true,
      minWidth: 180,
      type: "string",
      flex: 1,
    },
    {
      field: "unit",
      headerName: t("Unit"),
      disableColumnMenu: true,
      minWidth: 150,
      type: "string",
    },
    {
      field: "isRequireBaseYear",
      headerName: t("Base-Year"),
      disableColumnMenu: true,
      minWidth: 180,
      type: "boolean",
      renderCell: (row: any) => {
        return <Checkbox checked={row.value} disabled />;
      },
    },
    {
      field: "isDisable",
      headerName: t("activated"),
      disableColumnMenu: true,
      minWidth: 120,
      type: "boolean",
      renderCell: (params) => {
        return (
          <Switch
            id={"switch" + params.row._id}
            key={"switch" + params.row._id}
            checked={!params.value}
            onChange={() => enableDisableEconomicIndicators(params.row._id)}
            disabled={!isAdmin()}
          />
        );
      },
    },
    // {
    //   field: "attachment",
    //   headerName: t("Files"),
    //   sortable: false,
    //   ...
    // },
  ];

  const enableDisableEconomicIndicators = async (refId: string) => {
    let result;
    try {
      result = await EcoEfficiencyService.enableDisableEconomicIndicators(refId);
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      setEconomicIndicators(result);
      alertService.mixin(t("edit-successfully"));
    }
  };

  const quickUploadFile = async (base64: string, refId: string) => {
    let result;
    try {
      result = await EcoEfficiencyService.editEconomicIndicators(refId, {
        attachment: base64,
      });
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      alertService.mixin(t("edit-successfully"));
      setEconomicIndicators(result);
    }
  };

  const handleEditClick = (row: EconomicIndicator) => {
    setEditData(row);
    setModalIsOpen(true);
  };

  useEffect(() => {
    setRows(economicIndicators);
  }, [economicIndicators]);

  return (
    <>
      <AppDataGrid
        rows={rows}
        columns={columns}
        rowHeight={60}
        // ✅ 1. เพิ่ม onCellClick เพื่อดักจับการกดที่ Row
        onCellClick={(params) => {
          // ยกเว้นคอลัมน์ที่ไม่ต้องการให้คลิกแล้วเด้ง Edit (เช่น Switch เปิด/ปิดการใช้งาน, หรือคอลัมน์อัปโหลดไฟล์ในอนาคต)
          const ignoreColumns = [
            "isDisable",
            "attachment",
            "isRequireBaseYear",
          ];
          if (ignoreColumns.includes(params.field)) {
            return;
          }

          // ตรวจสอบสิทธิ์ว่ามีสิทธิ์แก้ไขหรือไม่ (ถ้าไม่ใช่ Admin ให้กดไม่ได้)
          if (!isAdmin()) {
            return;
          }

          // เรียกใช้ฟังก์ชัน Edit เดิม
          handleEditClick(params.row);
        }}
        slots={{ toolbar: QuickSearchToolbar }}
        localeText={{
          toolbarQuickFilterPlaceholder: `${t("search")}...`,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {
              variant: "outlined",
            },
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 50 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          ...tableStyle(),
          ...(!isAdmin() && { "& .MuiDataGrid-row": { cursor: "default" } }),
        }}
        getRowId={(row) => row._id}
      />
    </>
  );
};
